import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { isNativeApp } from "@/lib/nativeBridge";
import { enablePush, installId, pushStatus, setPushEnabled } from "@/lib/notificationsApi";

const LABELS = {
  granted: "Notificações ativadas neste aparelho",
  denied: "Notificações bloqueadas nos Ajustes do iPhone",
  undetermined: "Permissão ainda não solicitada",
  unsupported: "Os avisos no celular funcionam no aplicativo instalado no aparelho",
  error: "Não foi possível verificar a permissão agora",
};

/** Preferência de notificações do aplicativo; a ausência de permissão não impede o uso normal. */
export const PushPreferenceCard = ({ app, title, description, testId = "push-preference" }) => {
  const [state, setState] = useState({ loading: true, kind: "unsupported", token: null, enabled: false, error: "" });
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const refresh = useCallback(async () => {
    if (!isNativeApp()) {
      setState({ loading: false, kind: "unsupported", token: null, enabled: false, error: "" });
      return null;
    }
    try {
      const status = await pushStatus();
      const kind = status?.granted ? "granted" : status?.denied ? "denied" : "undetermined";
      setState({
        loading: false,
        kind,
        token: status?.token || null,
        enabled: Boolean(status?.granted && status?.token),
        projectId: status?.projectId,
        moduleReady: status?.moduleReady,
        error: "",
      });
      return status;
    } catch (error) {
      setState({ loading: false, kind: "error", token: null, enabled: false, error: String(error?.message || error) });
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activate = async () => {
    const result = await enablePush(app);
    if (!result.ok) {
      toast.error("Permita as notificações nos Ajustes do iPhone para receber os avisos.");
      setState((s) => ({ ...s, error: result.reason || "negado" }));
      await refresh();
      return;
    }
    setState((s) => ({ ...s, kind: "granted", token: result.token, enabled: true, error: "" }));
    toast.success("Avisos ativados neste aparelho");
  };

  const toggle = async (next) => {
    try {
      if (next) return await activate();
      if (state.token) await setPushEnabled(app, state.token, false);
      setState((s) => ({ ...s, enabled: false }));
      toast.success("Avisos desativados neste aparelho");
    } catch {
      toast.error("Não foi possível atualizar a preferência agora.");
    }
  };

  return (
    <section data-testid={testId} className="er-card">
      <p className="er-label">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-ink-600">{description}</p>

      <p data-testid={`${testId}-state`} className="mt-3 text-xs text-ink-400">
        {LABELS[state.kind]}
      </p>

      {state.kind === "granted" ? (
        <label className="mt-4 flex items-center justify-between gap-4">
          <span className="text-sm text-ink-700">Receber avisos neste aparelho</span>
          <Switch
            data-testid={`${testId}-switch`}
            checked={state.enabled}
            disabled={state.loading}
            onCheckedChange={toggle}
          />
        </label>
      ) : null}

      {state.kind === "undetermined" ? (
        <button
          type="button"
          data-testid={`${testId}-activate`}
          onClick={activate}
          className="mt-4 inline-flex min-h-[44px] items-center rounded-full bg-sage-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-sage-700"
        >
          Ativar notificações
        </button>
      ) : null}

      {state.kind === "denied" ? (
        <p data-testid={`${testId}-denied-help`} className="mt-3 rounded-xl bg-sand px-4 py-3 text-xs leading-relaxed text-ink-600">
          Abra Ajustes → Notificações → Erimus no seu iPhone e permita os avisos. Depois volte aqui e toque em atualizar.
        </p>
      ) : null}

      {isNativeApp() ? (
        <>
          <button
            type="button"
            data-testid={`${testId}-diagnostics-toggle`}
            onClick={async () => {
              await refresh();
              setShowDiagnostics((v) => !v);
            }}
            className="mt-3 text-[11px] text-ink-400 underline underline-offset-2"
          >
            {showDiagnostics ? "Ocultar diagnóstico" : "Atualizar e ver diagnóstico"}
          </button>
          {showDiagnostics ? (
            <dl data-testid={`${testId}-diagnostics`} className="mt-2 space-y-1 rounded-xl bg-sand px-4 py-3 text-[11px] text-ink-500">
              <div>Módulo de notificações: {state.moduleReady ? "disponível" : "indisponível"}</div>
              <div>Permissão do iOS: {state.kind}</div>
              <div>Token do aparelho: {state.token ? `recebido (final ${state.token.slice(-6, -1)})` : "não recebido"}</div>
              <div>Registro no servidor: {state.enabled ? "confirmado" : "pendente"}</div>
              <div>Projeto: {state.projectId || "não informado"}</div>
              <div>Instalação: {installId().slice(-8)}</div>
              {state.error ? <div>Último erro: {state.error}</div> : null}
            </dl>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default PushPreferenceCard;
